/**
 * Cronos POS Chain Staking Hooks
 *
 * React Query hooks for fetching and managing Cronos staking data
 */

import { useQuery } from '@tanstack/react-query';
import { useMemo } from 'react';
import { cronosApi } from '../services/cronosApi';
import type {
  CronosWalletData,
  CronosNetworkStats,
  CronosValidatorInfo,
  CronosAggregatedData,
} from '../types/cronos.types';

// ============================================
// PRICE HOOK
// ============================================

/**
 * Hook to fetch CRO price
 */
export function useCroPrice() {
  return useQuery({
    queryKey: ['cronos', 'price'],
    queryFn: () => cronosApi.getCroPrice(),
    staleTime: 60 * 1000, // 1 minute
    refetchInterval: 2 * 60 * 1000, // 2 minutes
    retry: 3,
  });
}

// ============================================
// NETWORK STATS HOOK
// ============================================

/**
 * Hook to fetch network staking statistics
 */
export function useCronosNetworkStats(croPrice: number) {
  return useQuery({
    queryKey: ['cronos', 'networkStats', croPrice],
    queryFn: () => cronosApi.getNetworkStats(croPrice),
    staleTime: 5 * 60 * 1000, // 5 minutes
    refetchInterval: 10 * 60 * 1000, // 10 minutes
    enabled: croPrice > 0,
    retry: 2,
  });
}

// ============================================
// VALIDATORS HOOK
// ============================================

/**
 * Hook to fetch all validators
 */
export function useCronosValidators(croPrice: number) {
  return useQuery({
    queryKey: ['cronos', 'validators', croPrice],
    queryFn: () => cronosApi.getValidatorsInfo(croPrice),
    staleTime: 5 * 60 * 1000, // 5 minutes
    refetchInterval: 10 * 60 * 1000, // 10 minutes
    enabled: croPrice > 0,
    retry: 2,
  });
}

// ============================================
// WALLET DATA HOOK
// ============================================

/**
 * Hook to fetch wallet data for a single address
 */
export function useCronosWallet(address: string, croPrice: number) {
  return useQuery({
    queryKey: ['cronos', 'wallet', address, croPrice],
    queryFn: () => cronosApi.getWalletData(address, croPrice),
    staleTime: 60 * 1000, // 1 minute
    refetchInterval: 5 * 60 * 1000, // 5 minutes
    enabled: !!address && cronosApi.isValidCronosAddress(address) && croPrice > 0,
    retry: 2,
  });
}

/**
 * Hook to fetch wallet data for multiple addresses
 */
export function useCronosWallets(addresses: string[], croPrice: number) {
  const validAddresses = addresses.filter(cronosApi.isValidCronosAddress);

  const query = useQuery({
    queryKey: ['cronos', 'wallets', validAddresses.join(','), croPrice],
    queryFn: () => cronosApi.getMultipleWalletsData(validAddresses, croPrice),
    staleTime: 60 * 1000, // 1 minute
    refetchInterval: 5 * 60 * 1000, // 5 minutes
    enabled: validAddresses.length > 0 && croPrice > 0,
    retry: 2,
  });

  // Aggregate data across all wallets
  const aggregatedData = useMemo<CronosAggregatedData | null>(() => {
    if (!query.data || query.data.length === 0) return null;

    const wallets = query.data;
    const totalAvailable = wallets.reduce((sum, w) => sum + w.availableBalance, 0);
    const totalStaked = wallets.reduce((sum, w) => sum + w.totalStaked, 0);
    const totalRewards = wallets.reduce((sum, w) => sum + w.totalRewards, 0);
    const totalUnbonding = wallets.reduce((sum, w) => sum + w.totalUnbonding, 0);
    const totalValue = wallets.reduce((sum, w) => sum + w.totalValue, 0);
    const totalValueUsd = wallets.reduce((sum, w) => sum + w.totalValueUsd, 0);

    // Unique validators across all wallets
    const uniqueValidatorAddresses = new Set<string>();
    wallets.forEach(w => {
      w.delegations.forEach(d => uniqueValidatorAddresses.add(d.validatorAddress));
    });

    // Weighted average APY
    let weightedApy = 0;
    if (totalStaked > 0) {
      wallets.forEach(w => {
        weightedApy += (w.totalStaked / totalStaked) * w.estimatedApy;
      });
    }

    return {
      wallets,
      totalAvailable,
      totalStaked,
      totalRewards,
      totalUnbonding,
      totalValue,
      totalValueUsd,
      uniqueValidators: uniqueValidatorAddresses.size,
      weightedApy,
    };
  }, [query.data]);

  return {
    ...query,
    aggregatedData,
  };
}

// ============================================
// COMBINED HOOK
// ============================================

/**
 * Main hook that combines all Cronos staking data
 */
export function useCronosStaking(addresses: string[]) {
  // Get CRO price
  const priceQuery = useCroPrice();
  const croPrice = priceQuery.data || 0;

  // Get network stats
  const networkStatsQuery = useCronosNetworkStats(croPrice);

  // Get wallet data
  const walletsQuery = useCronosWallets(addresses, croPrice);

  // Combined loading state
  const isLoading = priceQuery.isLoading ||
    (addresses.length > 0 && walletsQuery.isLoading);

  // Combined error
  const error = priceQuery.error || walletsQuery.error || networkStatsQuery.error;

  // Refetch all data
  const refetch = async () => {
    await Promise.all([
      priceQuery.refetch(),
      networkStatsQuery.refetch(),
      walletsQuery.refetch(),
    ]);
  };

  return {
    // Price
    croPrice,
    priceLoading: priceQuery.isLoading,

    // Network stats
    networkStats: networkStatsQuery.data,
    networkStatsLoading: networkStatsQuery.isLoading,

    // Wallet data
    wallets: walletsQuery.data || [],
    walletsLoading: walletsQuery.isLoading,
    aggregatedData: walletsQuery.aggregatedData,

    // Combined state
    isLoading,
    error,
    refetch,

    // Last updated
    lastUpdated: walletsQuery.dataUpdatedAt,
  };
}

// ============================================
// UTILITY HOOKS
// ============================================

/**
 * Hook to validate and format Cronos address
 */
export function useValidateCronosAddress(address: string) {
  const isValid = useMemo(() => {
    if (!address) return false;
    return cronosApi.isValidCronosAddress(address);
  }, [address]);

  const formatted = useMemo(() => {
    if (!isValid) return address;
    // Format: cro1xxx...xxx (show first 8 + last 6)
    return `${address.slice(0, 8)}...${address.slice(-6)}`;
  }, [address, isValid]);

  return { isValid, formatted };
}

/**
 * Hook to get top validators by voting power
 */
export function useTopValidators(croPrice: number, limit = 10) {
  const validatorsQuery = useCronosValidators(croPrice);

  const topValidators = useMemo(() => {
    if (!validatorsQuery.data) return [];
    return validatorsQuery.data.slice(0, limit);
  }, [validatorsQuery.data, limit]);

  return {
    validators: topValidators,
    isLoading: validatorsQuery.isLoading,
    error: validatorsQuery.error,
  };
}
